/**
 * Lighting Controller
 *
 * Monitors video brightness and contrast for optimal face verification conditions.
 * Provides real-time feedback to users about lighting quality.
 */

import { verificationState, LightingStatus } from '../state/verification-state';
import { i18n } from '../../i18n';

// Re-export type for convenience
export type { LightingStatus };

/**
 * Configuration for lighting quality thresholds (0-255 scale).
 * Values tuned for face verification scenarios.
 */
export interface LightingConfig {
  /** Below this = too dark. Default: 60 */
  minBrightness: number;
  /** Above this = too bright/washed out. Default: 220 */
  maxBrightness: number;
  /** Minimum standard deviation for good contrast. Default: 30 */
  minContrast: number;
  /** Ideal brightness lower bound. Default: 80 */
  idealBrightnessMin: number;
  /** Ideal brightness upper bound. Default: 180 */
  idealBrightnessMax: number;
  /** Sample every Nth pixel for performance. Default: 100 */
  sampleSize: number;
  /** Milliseconds between checks. Default: 500 */
  checkIntervalMs: number;
}

/**
 * Result of lighting analysis.
 */
export interface LightingResult {
  status: LightingStatus;
  brightness: number;
  contrast: number;
  message: string;
}

/**
 * Listener callback type for status changes.
 */
export type LightingStatusListener = (result: LightingResult) => void;

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: LightingConfig = {
  minBrightness: 60,
  maxBrightness: 220,
  minContrast: 30,
  idealBrightnessMin: 80,
  idealBrightnessMax: 180,
  sampleSize: 100,
  checkIntervalMs: 500,
};

/**
 * LightingController monitors video brightness and contrast.
 *
 * Features:
 * - Real-time analysis of video frames
 * - Continuous monitoring with configurable interval
 * - Status change notifications via listeners
 * - Integration with verification state
 */
export class LightingController {
  private config: LightingConfig;
  private intervalId: number | null = null;
  private currentStatus: LightingStatus = 'unknown';
  private listeners: Set<LightingStatusListener> = new Set();

  constructor(config: Partial<LightingConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Analyzes lighting quality from a video element.
   *
   * Uses luminosity-weighted grayscale conversion and samples pixels
   * for performance. Analyzes the center region where the face is expected.
   *
   * @param video - HTMLVideoElement to analyze
   * @returns LightingResult with status, brightness, contrast, and message
   */
  analyze(video: HTMLVideoElement): LightingResult {
    // Check if video is ready
    if (!video || video.readyState < 2) {
      const t = i18n.getTranslations();
      return {
        status: 'unknown',
        brightness: 0,
        contrast: 0,
        message: t.lighting.videoNotReady,
      };
    }

    try {
      // Create offscreen canvas for analysis (small for performance)
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      if (!ctx) {
        const t = i18n.getTranslations();
        return {
          status: 'unknown',
          brightness: 0,
          contrast: 0,
          message: t.errors.unknownError,
        };
      }

      // Use smaller size for performance
      const width = 160;
      const height = 120;
      canvas.width = width;
      canvas.height = height;

      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, width, height);

      // Get image data - focus on center region (face area)
      const centerX = Math.floor(width * 0.25);
      const centerY = Math.floor(height * 0.15);
      const regionWidth = Math.floor(width * 0.5);
      const regionHeight = Math.floor(height * 0.7);

      const imageData = ctx.getImageData(centerX, centerY, regionWidth, regionHeight);

      // Calculate brightness and contrast from pixel data
      const { brightness, contrast } = this.calculateBrightnessAndContrast(imageData.data);

      // Determine status and message based on thresholds
      return this.determineStatus(brightness, contrast);
    } catch (error) {
      console.error('[LightingController] Analysis error:', error);
      const t = i18n.getTranslations();
      return {
        status: 'unknown',
        brightness: 0,
        contrast: 0,
        message: t.errors.unknownError,
      };
    }
  }

  /**
   * Calculates brightness and contrast from pixel data.
   *
   * Uses luminosity-weighted grayscale conversion (0.299R + 0.587G + 0.114B)
   * which matches human eye sensitivity. Samples every Nth pixel for performance.
   *
   * @param pixels - Uint8ClampedArray of RGBA pixel data
   * @returns Object with brightness (mean) and contrast (std deviation)
   */
  private calculateBrightnessAndContrast(pixels: Uint8ClampedArray): {
    brightness: number;
    contrast: number;
  } {
    let sum = 0;
    let sumSquared = 0;
    let count = 0;

    // Sample pixels for performance (every SAMPLE_SIZE-th pixel)
    // Each pixel is 4 bytes (RGBA), so we step by 4 * sampleSize
    for (let i = 0; i < pixels.length; i += 4 * this.config.sampleSize) {
      // Convert to grayscale using luminosity method
      // This matches human eye sensitivity to different colors
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

      sum += luminance;
      sumSquared += luminance * luminance;
      count++;
    }

    if (count === 0) {
      return { brightness: 0, contrast: 0 };
    }

    const brightness = sum / count;
    const variance = sumSquared / count - brightness * brightness;
    const contrast = Math.sqrt(Math.max(0, variance)); // Standard deviation

    return {
      brightness: Math.round(brightness),
      contrast: Math.round(contrast),
    };
  }

  /**
   * Determines lighting status based on brightness and contrast values.
   *
   * @param brightness - Mean brightness value (0-255)
   * @param contrast - Contrast value (standard deviation)
   * @returns LightingResult with status and user-friendly message
   */
  private determineStatus(brightness: number, contrast: number): LightingResult {
    const t = i18n.getTranslations();
    let status: LightingStatus = 'good';
    let message = t.lighting.improveLighting;

    if (brightness < this.config.minBrightness) {
      status = 'too_dark';
      message = t.lighting.lowLight;
    } else if (brightness > this.config.maxBrightness) {
      status = 'too_bright';
      message = t.lighting.tooBright;
    } else if (contrast < this.config.minContrast) {
      status = 'low_contrast';
      message = t.lighting.poorContrast;
    } else if (
      brightness >= this.config.idealBrightnessMin &&
      brightness <= this.config.idealBrightnessMax
    ) {
      status = 'good';
      message = t.face.good;
    }

    return { status, brightness, contrast, message };
  }

  /**
   * Starts continuous lighting monitoring for a video element.
   *
   * @param video - HTMLVideoElement to monitor
   */
  start(video: HTMLVideoElement): void {
    this.stop(); // Clear any existing interval

    console.log('[LightingController] Starting continuous monitoring');

    // Immediate first check
    const initialResult = this.analyze(video);
    this.updateStatus(initialResult);

    // Continuous monitoring
    this.intervalId = window.setInterval(() => {
      const result = this.analyze(video);

      // Only trigger listeners if status changed
      if (result.status !== this.currentStatus) {
        console.log(
          '[LightingController] Status changed:',
          this.currentStatus,
          '->',
          result.status
        );
        this.updateStatus(result);
      }
    }, this.config.checkIntervalMs);
  }

  /**
   * Stops lighting monitoring.
   */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    console.log('[LightingController] Stopped monitoring');
  }

  /**
   * Updates current status and notifies listeners.
   */
  private updateStatus(result: LightingResult): void {
    this.currentStatus = result.status;

    // Update verification state
    verificationState.update('lighting', {
      status: result.status,
      blocksProgress: !this.isAcceptable(),
    });

    // Notify listeners
    for (const listener of this.listeners) {
      try {
        listener(result);
      } catch (error) {
        console.error('[LightingController] Listener error:', error);
      }
    }
  }

  /**
   * Checks if current lighting is acceptable for continuing.
   *
   * @returns true if lighting is good or unknown (haven't checked yet)
   */
  isAcceptable(): boolean {
    return this.currentStatus === 'good' || this.currentStatus === 'unknown';
  }

  /**
   * Gets the current lighting status.
   */
  getStatus(): LightingStatus {
    return this.currentStatus;
  }

  /**
   * Performs a one-time lighting check.
   *
   * @param video - HTMLVideoElement to check
   * @returns LightingResult with acceptable flag
   */
  checkOnce(video: HTMLVideoElement): LightingResult & { acceptable: boolean } {
    const result = this.analyze(video);
    return {
      ...result,
      acceptable: result.status === 'good',
    };
  }

  /**
   * Subscribes to lighting status changes.
   *
   * @param listener - Callback function
   * @returns Unsubscribe function
   */
  onStatusChange(listener: LightingStatusListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Gets the number of registered listeners.
   */
  get listenerCount(): number {
    return this.listeners.size;
  }

  /**
   * Disposes of the controller, stopping any monitoring.
   */
  dispose(): void {
    this.stop();
    this.listeners.clear();
  }
}

/**
 * Singleton instance for convenience.
 */
export const lightingController = new LightingController();
